import React, { useContext, useState } from "react";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import { List, Divider, Button, Grid, CircularProgress, Typography, Box, FormControl, Select, MenuItem, Input, Checkbox, ListItemText } from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import Layout from "../components/Layout/Layout";
import ErrorPage from "./ErrorPage";
import WorklineListItem from "../components/WorklineListItem/WorklineListItem";
import { useGetInspectionLinesByIdQuery, useGetAdminInspectionLinesQuery } from "../api";
import { PRIVILEGES } from "../constants";
import { hasPrivilege } from "../utils/privilegeUtils";
import { UserDataContext } from "../app/App";

const filters = ["desc", "asc"];
const sortDates = (arr, key, order = "asc") => {
    const sortedArr = [...arr]; // to avoid mutating the original array

    sortedArr.sort((a, b) => {
        const dateA = dayjs(a[key]);
        const dateB = dayjs(b[key]);

        if (order === "asc") {
            return dateA - dateB;
        } else {
            return dateB - dateA;
        }
    });

    return sortedArr;
};

function Inspection() {
    const [isAdminView, setIsAdminView] = useState(false);

    const [filter, setFilter] = useState(filters[0]);
    const [filterByPostNums, setIsFilterByPostNums] = useState(false);
    const [postNum, setPostNum] = useState([]);

    const history = useHistory();
    const { user } = useContext(UserDataContext);
    const isAdmin = hasPrivilege(user.crmPrivileges, PRIVILEGES.INSPECTION_SUPER);

    const { data: inspectionLinesById, isLoading: isLoadingInspectionLinesById, isError: isErrorInspectionLinesById } = useGetInspectionLinesByIdQuery(user.id);

    const {
        data: adminInspectionLines,
        isLoading: isLoadingAdminInspectionLines,
        isError: isErrorAdminInspectionLines,
        refetch: refetchAdminInspectionLines,
    } = useGetAdminInspectionLinesQuery(isAdmin);

    const handleChangePostNum = (event) => {
        setPostNum(event.target.value);
    };

    const handleAdminToggle = () => {
        setIsAdminView(!isAdminView);
        if (!isAdminView) refetchAdminInspectionLines();
    };

    if (isErrorInspectionLinesById || isErrorAdminInspectionLines) {
        return <ErrorPage />;
    }

    if (isLoadingInspectionLinesById || isLoadingAdminInspectionLines) {
        return (
            <Layout title={`Inspection`} subtitle={user.name}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    const handleLineClick = (lineId) => {
        history.push(`/inspection/${lineId}`);
    };

    const inspectionLines = (isAdmin && isAdminView ? adminInspectionLines : inspectionLinesById) || [];
    const sortedLines = sortDates(inspectionLines, "createdOn", filter);

    const inspectionLinePostalCodes = inspectionLines.filter((line) => line?.projectLine?.project?.address?.postalCode).map((line) => line.projectLine.project.address.postalCode);

    const uniquePostalCodes = [...new Set(inspectionLinePostalCodes)];
    const displayedLines = filterByPostNums ? sortedLines.filter((line) => postNum.includes(line?.projectLine?.project?.address?.postalCode)) : sortedLines;

    const totalCount = displayedLines ? displayedLines.length : 0;

    return (
        <Layout title={`Inspection`} subtitle={user.name} onAdminToggle={isAdmin ? handleAdminToggle : null}>
            <Grid container direction="column" spacing={2}>
                <Grid item container direction="column" spacing={1}>
                    <Grid item>
                        <Button
                            onClick={() => setFilter("desc")}
                            size="small"
                            variant={filter === "desc" ? "contained" : "outlined"}
                            style={filter === "desc" ? { borderRadius: 50, color: "#fff", backgroundColor: "#000", marginRight: 10 } : { borderRadius: 50 }}
                        >
                            {`Newest`}
                        </Button>

                        <Button
                            onClick={() => setFilter("asc")}
                            size="small"
                            variant={filter === "asc" ? "contained" : "outlined"}
                            style={filter === "asc" ? { borderRadius: 50, color: "#fff", backgroundColor: "#000", marginLeft: 10 } : { borderRadius: 50 }}
                        >
                            {`Oldest`}
                        </Button>
                    </Grid>

                    <Grid item container direction="column">
                        <Grid item>
                            <Button
                                onClick={() => setIsFilterByPostNums((prev) => !prev)}
                                size="small"
                                startIcon={<FilterListIcon />}
                                variant={filterByPostNums ? "contained" : "outlined"}
                                style={filterByPostNums ? { borderRadius: 50, color: "#fff", backgroundColor: "#000", marginRight: 10 } : { borderRadius: 50 }}
                            >
                                {`Post Numbers`}
                            </Button>
                        </Grid>

                        <Grid item>
                            <Box px={1}>
                                <FormControl fullWidth disabled={!filterByPostNums}>
                                    <Select multiple displayEmpty value={postNum} onChange={handleChangePostNum} input={<Input />} renderValue={(selected) => selected.join(", ")}>
                                        {uniquePostalCodes.map((pn) => (
                                            <MenuItem key={pn} value={pn}>
                                                <Checkbox checked={postNum.indexOf(pn) > -1} />
                                                <ListItemText primary={pn} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item>
                    <Typography variant="subtitle1">
                        <Typography variant="h5" component="span" color="primary" style={{ fontWeight: "bold", paddingRight: "8px" }}>
                            {totalCount}
                        </Typography>
                        {totalCount === 1 ? "Inspection Request" : "Inspection Requests"}
                    </Typography>
                </Grid>

                <Grid item>
                    <Divider style={{ backgroundColor: "darkgray" }} />
                </Grid>
                <Grid item>
                    <List>
                        {displayedLines &&
                            displayedLines.map((line) => (
                                <Box key={line.id} pb={1}>
                                    <WorklineListItem line={line} onClick={() => handleLineClick(line.id)} />
                                    <Divider />
                                </Box>
                            ))}
                    </List>
                </Grid>
            </Grid>
        </Layout>
    );
}

export default Inspection;
